import React, { useEffect, useState } from "react";
import { http, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, ArrowRight, Stethoscope, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DOTS = ["bg-brand-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-brand-500"];

export default function CompanySelect() {
  const [companies, setCompanies] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", gstin: "", city: "", state: "Telangana", state_code: "36" });
  const [delTarget, setDelTarget] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await http.get("/companies");
      setCompanies(data);
    } catch (error) {
      setLoadError(requestErrorMessage(error, "load companies"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const pick = (c) => {
    localStorage.setItem("vg_company", c.id);
    localStorage.setItem("vg_company_name", c.name);
    window.location.href = "/";
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error("Company name required");
    try { const { data } = await http.post("/companies", form); toast.success("Company created"); pick(data); }
    catch (e) { toast.error(requestErrorMessage(e, "create this company")); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await http.delete(`/companies/${delTarget.id}`);
      toast.success("Company deleted");
      if (localStorage.getItem("vg_company") === delTarget.id) {
        localStorage.removeItem("vg_company"); localStorage.removeItem("vg_company_name");
      }
      setDelTarget(null); load();
    } catch (e) { toast.error(requestErrorMessage(e, "delete this company")); }
    finally { setDeleting(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-background">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2.5 mb-6 justify-center">
          <div className="h-10 w-10 rounded-lg bg-brand grid place-items-center shadow-sm"><Stethoscope className="h-5 w-5 text-brand-foreground" /></div>
          <span className="font-heading font-bold text-lg">VGRUPS ERP</span>
        </div>
        <Card className="p-6">
          <h1 className="font-heading text-xl font-bold">Select Company</h1>
          <p className="text-sm text-muted-foreground mt-1">Choose a company to enter its workspace</p>
          {loadError && <div role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{loadError}<Button variant="outline" size="sm" className="ml-2" onClick={load}>Retry</Button></div>}
          <div className="mt-5 space-y-3">
            {loading && <div role="status" className="text-sm text-muted-foreground text-center py-4">Loading companies…</div>}
            {companies.map((c, i) => (
              <div key={c.id} className="relative">
                <button onClick={() => pick(c)} data-testid={`company-${c.id}`}
                  className="w-full flex items-center gap-3 p-4 pr-20 rounded-lg border border-slate-200 hover:border-brand-400 hover:bg-brand-50/50 transition-colors text-left">
                  <span className={`h-3 w-3 rounded-full ${DOTS[i % DOTS.length]}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.gstin || "No GSTIN"} · {c.city || c.state} {c.role ? `· ${c.role}` : ""}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </button>
                <button onClick={() => { setDelTarget(c); setConfirmName(""); }} data-testid={`delete-company-${c.id}`}
                  title="Delete company" className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-rose-50">
                  <Trash2 className="h-4 w-4 text-rose-400" />
                </button>
              </div>
            ))}
            {!loading && !loadError && companies.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">No companies yet — create one below</div>}
          </div>

          {creating ? (
            <div className="mt-5 border-t pt-5 space-y-3">
              <div><Label>Company Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="new-company-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="mt-1.5 font-mono" /></div>
                <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button><Button onClick={create} data-testid="create-company-submit">Create &amp; Enter</Button></div>
            </div>
          ) : (
            <Button variant="outline" className="w-full mt-5" onClick={() => setCreating(true)} data-testid="create-company-button"><Plus className="h-4 w-4 mr-1" /> Create Company</Button>
          )}
        </Card>
      </div>

      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Delete Company</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This permanently deletes <span className="font-semibold text-foreground">{delTarget?.name}</span> and all its data (invoices, parties, inventory, users). This cannot be undone.</p>
          <div className="mt-1"><Label>Type the company name to confirm</Label>
            <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} className="mt-1.5" placeholder={delTarget?.name} data-testid="confirm-company-name" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={deleting || confirmName !== delTarget?.name}
              onClick={doDelete} data-testid="confirm-delete-company">{deleting ? "Deleting…" : "Delete Company"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
